import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    ListGroup, ListGroupItem, Modal,
    ModalBody, ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Row,
    Spinner,
    TabContent,
    TabPane,
    UncontrolledDropdown
} from 'reactstrap';

import {ClassroomIndividualAPI, ClassRoomInsideAPI} from "api";
import {Link, useParams, useHistory} from "react-router-dom";
import {
    DeletePost, EditPost, Utils
} from "custom";

import SimpleBar from "simplebar-react";
import VoiceRecorder from "../../../Components/Common/VoiceRecorder";
import VoiceMessagePlayer from "../../../Components/Common/VoiceMessagePlayer";

const moment = require("moment");

const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 25;

const isAllowedFile = (file) => {
    const type = (file?.type || "").toLowerCase();
    return type === "application/pdf" || type.startsWith("image/") || type.startsWith("audio/");
};

const prettySize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = bytes / 1024;
    return `${Math.round(kb)} KB`;
};

const isPdfName = (name = "") => name.toLowerCase().endsWith(".pdf");
const isImageName = (name = "") =>
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
const isAudioName = (name = "") =>
    /\.(webm|mp3|mp4|m4a|ogg|oga|wav|aac)$/i.test(name);

const BoardInside = ({posts, classroomId, onResolve}) => {
    const history = useHistory()

    const [loadingButton, setLoadingButton] = useState(false);
    const [classroom, setClassroom] = useState(null);

    const [error, setError] = useState(false);

    const [message, setMessage] = useState("");

    const [attachments, setAttachments] = useState([]);
    const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [attachError, setAttachError] = useState("");

    const [streamingFileId, setStreamingFileId] = useState(null);

    // Cache: id_file -> blob URL (per riprodurre audio inline senza ricaricare ad ogni play)
    const [audioUrls, setAudioUrls] = useState({});
    const [audioLoading, setAudioLoading] = useState({});

    // Flag: se true, sto inviando un vocale (post di solo audio senza testo)
    const [sendingVoice, setSendingVoice] = useState(false);

    const isAdmin = (Utils.getUserData().role === "ADMIN");
    const isTeacher = (Utils.getUserData().role === "TEACHER");

    const {id} = useParams();

    const simpleBarRef = useRef(null);
    const fileInputRef = useRef(null);


    useEffect(() => {
        if (simpleBarRef.current) {
            const scrollEl = simpleBarRef.current.getScrollElement();
            scrollEl.scrollTop = scrollEl.scrollHeight;
        }
    }, [posts]);


    const toggleAttachModal = () => {
        setAttachError("");
        setIsDragging(false);
        setIsAttachModalOpen((v) => !v);
    };

    const openFilePicker = () => {
        setAttachError("");
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const normalizeAndAddFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        setAttachError("");

        const allowed = [];
        const rejected = [];

        for (const f of files) {
            if (!isAllowedFile(f)) {
                rejected.push(`${f.name} (tipo non supportato)`);
                continue;
            }
            const sizeMb = f.size / (1024 * 1024);
            if (sizeMb > MAX_FILE_SIZE_MB) {
                rejected.push(`${f.name} (troppo grande, max ${MAX_FILE_SIZE_MB}MB)`);
                continue;
            }
            allowed.push(f);
        }

        if (rejected.length) {
            setAttachError(`Alcuni file non sono stati aggiunti: ${rejected.join(", ")}`);
        }

        if (!allowed.length) return;

        setAttachments((prev) => {
            const remainingSlots = Math.max(0, MAX_FILES - prev.length);
            const toTake = allowed.slice(0, remainingSlots);

            if (allowed.length > remainingSlots) {
                setAttachError(
                    `Hai raggiunto il limite di ${MAX_FILES} allegati. Alcuni file non sono stati aggiunti.`
                );
            }

            const mapped = toTake.map((file) => ({
                id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                file,
            }));

            return [...prev, ...mapped];
        });
    };

    const onFileInputChange = (e) => {
        normalizeAndAddFiles(e.target.files);
        e.target.value = "";
    };

    const removeAttachment = (attId) => {
        setAttachments((prev) => prev.filter((a) => a.id !== attId));
    };

    const clearAllAttachments = () => {
        setAttachments([]);
        setAttachError("");
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setAttachError("");

        if (e.dataTransfer?.files?.length) {
            normalizeAndAddFiles(e.dataTransfer.files);
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const canSend = useMemo(
        () => message.trim() !== "" || attachments.length > 0,
        [message, attachments]
    );


    const sendMessage = async () => {

        // Si può inviare con testo O con almeno un allegato (allineato a canSend)
        if (message.trim() === "" && attachments.length === 0)
            return;

        setLoadingButton(true)
        setError(false);

        try {
            // Backend richiede 'message' non vuoto: se l'utente ha solo
            // allegati senza testo, uso un placeholder discreto.
            const finalMessage = (message.trim() === "") ? "📎 Allegato" : message;

            const id_post = await ClassRoomInsideAPI.postAdd({
                id_classroom: classroomId,
                message: finalMessage,
            });

            if (attachments.length) {
                for (const att of attachments) {
                    await ClassRoomInsideAPI.uploadPostFile(att.file, id_post);
                }
            }

            onResolve();
            setMessage("");
            setAttachments([]);
            setAttachError("");
        } catch (err) {
            setError(true);
            console.error(err);
        } finally {
            setLoadingButton(false);
        }
    }

    const openPostFile = async (id_file) => {
        if (!id_file) return;

        try {
            setStreamingFileId(id_file);

            const url = await ClassRoomInsideAPI.streamPostFile(id_file);

            const w = window.open(url, "_blank", "noopener,noreferrer");

            setTimeout(() => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    console.error("revokeObjectURL", e)
                }
            }, 60_000);
        } catch (e) {
            console.error(e);
        } finally {
            setStreamingFileId(null);
        }
    };

    // Carica l'audio inline e popola la cache
    const ensureAudioUrl = async (id_file) => {
        if (audioUrls[id_file]) return;
        try {
            setAudioLoading((prev) => ({ ...prev, [id_file]: true }));
            const url = await ClassRoomInsideAPI.streamPostFile(id_file);
            setAudioUrls((prev) => ({ ...prev, [id_file]: url }));
        } catch (e) {
            console.error("[audio] load error", e);
        } finally {
            setAudioLoading((prev) => ({ ...prev, [id_file]: false }));
        }
    };

    // Invia un vocale come post a sé stante (senza testo, 1 solo allegato audio)
    const onVoiceRecorded = async (audioFile) => {
        if (!audioFile) return;

        try {
            setSendingVoice(true);
            const id_post = await ClassRoomInsideAPI.postAdd({
                id_classroom: classroomId,
                message: "🎤 Vocale"
            });
            await ClassRoomInsideAPI.uploadPostFile(audioFile, id_post);
            onResolve();
        } catch (e) {
            console.error("[voice] send error", e);
            setError(true);
        } finally {
            setSendingVoice(false);
        }
    };


    return (
        <React.Fragment>
            <Row>
                <Col xl={12}>
                    <Card>
                        <CardHeader className="align-items-center d-flex">
                            <h4 className="card-title mb-0 flex-grow-1">Commenti Bacheca</h4>
                        </CardHeader>
                        {(error) ? <Alert color="danger" className="mx-3 mt-3 mb-0">
                            Non è stato possibile inviare il messaggio. Riprova più tardi.
                        </Alert> : null}

                        <CardBody>
                            <SimpleBar ref={simpleBarRef} style={{ height: "400px", overflowX: "hidden" }} className="px-3 mx-n3 mb-2">
                                {posts.map((p, i) => {
                                    const files = Array.isArray(p.FilePosts)
                                        ? p.FilePosts
                                        : [];

                                    return <div key={i} className="d-flex mb-4">
                                        <div className="team-profile-img">
                                            <div className="avatar-sm img-thumbnail rounded-circle">
                                                {(p.User.role === "CLIENT") ? <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                                    {p.User.firstname[0]}{p.User.lastname[0]}
                                                </div> : <div className="avatar-title bg-white rounded-circle">
                                                    <img src={require("../../../assets/images/avatar-cltv.png")} alt="" className="avatar-sm" />
                                                </div>}
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h5 className="fs-13">
                                                {p.User.role === "TEACHER" ? (
                                                    <i className="mdi mdi-school align-bottom me-1"></i>
                                                ) : null}{" "}
                                                {p.User.firstname} {p.User.lastname}{" "}
                                                <small className="text-muted ms-2">
                                                    {moment(p.createdAt).format("DD MMM YYYY HH:mm")}
                                                </small>
                                            </h5>

                                            <p className="text-muted mb-2">
                                                {p.message.split("\n").map((line, index) => {
                                                    const regex = /(https?:\/\/[^\s]+)/g;
                                                    const parts = line.split(regex);

                                                    return (
                                                        <React.Fragment key={index}>
                                                            {parts.map((part, ii) =>
                                                                regex.test(part) ? (
                                                                    <a
                                                                        key={ii}
                                                                        href={part}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {part}
                                                                    </a>
                                                                ) : (
                                                                    part
                                                                )
                                                            )}
                                                            <br />
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </p>

                                            {files.length > 0 && (
                                                <div className="mt-1">
                                                    <div className="text-muted mb-1">
                                                        <i className="mdi mdi-paperclip me-1"></i>
                                                        Allegati ({files.length})
                                                    </div>

                                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                                        {files.map((f) => {
                                                            const name = f.file_name || "file";

                                                            // Audio: player WhatsApp-style inline
                                                            if (isAudioName(name)) {
                                                                if (!audioUrls[f.id] && !audioLoading[f.id]) {
                                                                    ensureAudioUrl(f.id);
                                                                }
                                                                return (
                                                                    <VoiceMessagePlayer
                                                                        key={f.id}
                                                                        src={audioUrls[f.id]}
                                                                        loading={!!audioLoading[f.id] || !audioUrls[f.id]}
                                                                        filename={name}
                                                                    />
                                                                );
                                                            }

                                                            const badgeType = isPdfName(name)
                                                                ? "PDF"
                                                                : isImageName(name)
                                                                    ? "IMG"
                                                                    : "FILE";
                                                            const badgeColor = isPdfName(name)
                                                                ? "danger"
                                                                : isImageName(name)
                                                                    ? "info"
                                                                    : "secondary";

                                                            const isLoadingThis = streamingFileId === f.id;

                                                            return (
                                                                <button
                                                                    key={f.id}
                                                                    type="button"
                                                                    className="btn btn-sm btn-light border d-inline-flex align-items-center"
                                                                    style={{ maxWidth: "100%" }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        openPostFile(f.id);
                                                                    }}
                                                                    disabled={!!streamingFileId}
                                                                    title="Apri file"
                                                                >
                                                                    <Badge color={badgeColor} className="me-2">
                                                                        {badgeType}
                                                                    </Badge>

                                                                    <span
                                                                        className="text-truncate"
                                                                        style={{ maxWidth: 280 }}
                                                                    >
                                                                          {name}
                                                                        </span>

                                                                    {isLoadingThis && (
                                                                        <Spinner
                                                                            size="sm"
                                                                            className="ms-2"
                                                                            style={{ width: 14, height: 14 }}
                                                                        />
                                                                    )}

                                                                    {!isLoadingThis && (
                                                                        <i className="mdi mdi-open-in-new ms-2"></i>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {(p.User.id === Utils.getUserData().id || isAdmin)
                                            ?
                                            <div>
                                                <EditPost preset={p} onResolve={onResolve} label='Modifica Commento'/>
                                                &nbsp;&nbsp;&nbsp;
                                                <DeletePost id={p.id} onResolve={onResolve}/>
                                            </div>
                                            :
                                            null
                                        }
                                    </div>
                                })}
                            </SimpleBar>
                            <form className="mt-4" onSubmit={(e) => e.preventDefault()}>
                                <Row className="g-3">
                                    <Col xs={12}>
                                        <label
                                            htmlFor="exampleFormControlTextarea1"
                                            className="form-label text-body"
                                        >
                                            Lascia un Commento
                                        </label>

                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            className="form-control bg-light border-light"
                                            id="exampleFormControlTextarea1"
                                            rows="3"
                                            placeholder="Inserisci il tuo messaggio..."
                                        ></textarea>

                                        {attachments.length > 0 && (
                                            <div className="mt-2">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="text-muted">
                                                        <i className="mdi mdi-paperclip me-1"></i>
                                                        <span>
                                                          Allegati ({attachments.length}/{MAX_FILES})
                                                        </span>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-ghost-secondary shadow-none"
                                                            onClick={toggleAttachModal}
                                                        >
                                                            Aggiungi
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-ghost-danger shadow-none"
                                                            onClick={clearAllAttachments}
                                                        >
                                                            Rimuovi tutti
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {attachments.map((att) => {
                                                        const f = att.file;
                                                        const isPdf =
                                                            (f.type || "").toLowerCase() === "application/pdf";

                                                        return (
                                                            <div
                                                                key={att.id}
                                                                className="d-inline-flex align-items-center border rounded px-2 py-1 bg-white"
                                                                style={{ maxWidth: "100%" }}
                                                            >
                                                                <Badge
                                                                    color={isPdf ? "danger" : "info"}
                                                                    className="me-2"
                                                                >
                                                                    {isPdf ? "PDF" : "IMG"}
                                                                </Badge>

                                                                <span
                                                                    className="text-truncate"
                                                                    style={{ maxWidth: 280 }}
                                                                    title={f.name}
                                                                >
                                                                  {f.name}
                                                                </span>

                                                                <span className="text-muted ms-2">
                                                                  {prettySize(f.size)}
                                                                </span>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-ghost-danger btn-icon ms-2 shadow-none"
                                                                    onClick={() => removeAttachment(att.id)}
                                                                    aria-label="Rimuovi allegato"
                                                                    title="Rimuovi"
                                                                >
                                                                    <i className="mdi mdi-close"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {attachError && (
                                                    <Alert color="warning" className="mt-2 mb-0 py-2">
                                                        {attachError}
                                                    </Alert>
                                                )}
                                            </div>
                                        )}
                                    </Col>

                                    <Col xs={12} className="text-end">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="application/pdf,image/*,audio/*"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={onFileInputChange}
                                        />

                                        {(Utils.getUserData().role !== "CONSULENT") ? <button
                                            type="button"
                                            className="btn btn-ghost-secondary btn-icon waves-effect me-1 shadow-none"
                                            onClick={toggleAttachModal}
                                            title="Allega file"
                                        >
                                            <i className="mdi mdi-attachment fs-20"></i>
                                        </button> : null}

                                        <VoiceRecorder
                                            disabled={loadingButton || sendingVoice}
                                            maxSeconds={120}
                                            onRecorded={onVoiceRecorded}
                                        />

                                        <button
                                            disabled={!canSend || loadingButton || sendingVoice}
                                            onClick={sendMessage}
                                            type="button"
                                            className="btn btn-success"
                                        >
                                            {loadingButton || sendingVoice ? <Spinner /> : <b>Invia</b>}
                                        </button>
                                    </Col>
                                </Row>
                            </form>

                            <Modal
                                isOpen={isAttachModalOpen}
                                toggle={toggleAttachModal}
                                centered
                                size="lg"
                            >
                                <ModalHeader toggle={toggleAttachModal}>
                                    Allegati (PDF, Immagini o Audio)
                                </ModalHeader>

                                <ModalBody>
                                    <div
                                        onDrop={onDrop}
                                        onDragOver={onDragOver}
                                        onDragLeave={onDragLeave}
                                        className={`border rounded p-4 text-center ${
                                            isDragging ? "bg-light" : ""
                                        }`}
                                        style={{
                                            borderStyle: "dashed",
                                            transition: "all .15s ease-in-out",
                                        }}
                                    >
                                        <div className="mb-2">
                                            <i className="mdi mdi-cloud-upload-outline fs-1"></i>
                                        </div>
                                        <h5 className="mb-1">
                                            Trascina qui i file oppure clicca su “Seleziona file”
                                        </h5>
                                        <div className="text-muted">
                                            Supportati: <b>PDF</b>, <b>Immagini</b> e <b>Audio</b> • Max {MAX_FILES} file •
                                            Max {MAX_FILE_SIZE_MB}MB per file
                                        </div>

                                        <div className="mt-3 d-flex justify-content-center gap-2">
                                            <Button
                                                color="primary"
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openFilePicker();
                                                }}
                                            >
                                                Seleziona file
                                            </Button>
                                        </div>
                                    </div>

                                    {attachError && (
                                        <Alert color="warning" className="mt-3 mb-0">
                                            {attachError}
                                        </Alert>
                                    )}

                                    <div className="mt-4">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <h6 className="mb-0">File in coda ({attachments.length})</h6>
                                            {attachments.length > 0 && (
                                                <Button
                                                    color="danger"
                                                    outline
                                                    size="sm"
                                                    type="button"
                                                    onClick={clearAllAttachments}
                                                >
                                                    Rimuovi tutti
                                                </Button>
                                            )}
                                        </div>

                                        {attachments.length === 0 ? (
                                            <div className="text-muted">Nessun file selezionato.</div>
                                        ) : (
                                            <ListGroup>
                                                {attachments.map((att) => {
                                                    const f = att.file;
                                                    const isPdf =
                                                        (f.type || "").toLowerCase() === "application/pdf";

                                                    return (
                                                        <ListGroupItem
                                                            key={att.id}
                                                            className="d-flex align-items-center justify-content-between"
                                                        >
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge color={isPdf ? "danger" : "info"}>
                                                                    {isPdf ? "PDF" : "IMG"}
                                                                </Badge>
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-semibold">{f.name}</span>
                                                                    <span className="text-muted">
                                                                    {prettySize(f.size)}
                                                                  </span>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                color="danger"
                                                                outline
                                                                size="sm"
                                                                type="button"
                                                                onClick={() => removeAttachment(att.id)}
                                                            >
                                                                Rimuovi
                                                            </Button>
                                                        </ListGroupItem>
                                                    );
                                                })}
                                            </ListGroup>
                                        )}
                                    </div>
                                </ModalBody>

                                <ModalFooter className="d-flex justify-content-between">
                                    <div className="text-muted">
                                        Suggerimento: puoi trascinare i file nell’area tratteggiata.
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="secondary"
                                            outline
                                            type="button"
                                            onClick={toggleAttachModal}
                                        >
                                            Chiudi
                                        </Button>
                                        <Button color="success" type="button" onClick={toggleAttachModal}>
                                            Fatto
                                        </Button>
                                    </div>
                                </ModalFooter>
                            </Modal>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default BoardInside;
